import React, { useEffect, useState } from "react";
import CardFooter from "components/Card/CardFooter";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import SystemFailureIcon from "@material-ui/icons/ReportProblem";
import TextLoop from 'react-text-loop';
import WarningIcon from "@material-ui/icons/WarningRounded";
import dashboardStyle from "../../../assets/jss/material-dashboard-pro-react/views/dashboardStyle";

const noneOrUnknown = (lKTL) => lKTL === "none" || lKTL === "unknown_or_NA";
const textLoopStyle = { padding: "0", fontWeight: "500", color: "#ab30db" };
const deviceLostStyle = { padding: "0", fontWeight: "500", color: "#f44336" };
const nodeAcceleratedStyle = { padding: "0", fontWeight: "500", color: "#2525c2" };
const cardFooterStyle = { marginTop: "10px", color: "#6c6c6c", fontSize: "12px", flexWrap: "wrap", flexShrink: 1, flexBasis: "auto"};
const longTextStyle = { wordWrap: "break-word", overflowWrap: "anywhere", whiteSpace: "normal"};

const NodeStatusFooter = ({ node, classes, system }) => {
  const [isDeviceLost, setIsDeviceLost] = useState(null);
  const [isMeasurementsOutdated, setIsMeasurementsOutdated] = useState(null);
  const [isNodeAccelerated, setIsNodeAccelerated] = useState(node.AcceleratedUntil != null && Date.parse(node.AcceleratedUntil) > Date.now());
  const canMeasure = node.CanMeasure || node.DeviceRole === 'retransmitter' || node.DeviceRole === 'access_device'  && node.DeviceType !== 'UD-10.1.1';

  useEffect(() => {
    const latestPacketTimestamp = node.LatestPacketTimestamp;
    const latestMeasurementTimestamp = node.LatestMeasurementTimestamp;
    const w2ConnectionStatus = node.W2ConnectionStatus;
    const systemVersion = node.SystemVersion;
    const currentDate = Date.now();
    const latestPacketTimestampDate = Date.parse(latestPacketTimestamp);
    const latestMeasurementTimestampDate = Date.parse(latestMeasurementTimestamp);
    const isOperational = systemVersion !== 'W2' || node.W2Operational === true;
    //24 hours in milliseconds:
    const showWarningTime = 1000 * 60 * 60 * 24;
    if ((systemVersion === 'W2' && w2ConnectionStatus === 3 || systemVersion !== 'W2' && ((currentDate - latestPacketTimestampDate) > showWarningTime)) && isOperational) {
      setIsDeviceLost(true)
    } else {
      setIsDeviceLost(false)
    }
    if ((((currentDate - latestMeasurementTimestampDate) > showWarningTime/2)) && canMeasure && isOperational) {
      setIsMeasurementsOutdated(true)
    } else {
      setIsMeasurementsOutdated(false)
    }
    setIsNodeAccelerated(node.AcceleratedUntil != null && Date.parse(node.AcceleratedUntil) > Date.now());
  }, [node.LatestPacketTimestamp, node.W2ConnectionStatus, node.SystemVersion, node.AcceleratedUntil, node.W2Operational, node.canMeasure, node.updatedAt, node.LatestMeasurementTimestamp])


  const { stats } = dashboardStyle;
  let { deviceDamage, ObstructionStatus, BaseDistanceCorrection, deviceDamagedFrom, deviceDamagedInfo, AcceleratedUntil, outOfRange } = node;
  deviceDamage = deviceDamage && canMeasure;
  const lostSince = isDeviceLost ? (node.SystemVersion === 'W2' ? node.LatestSuccessfulMeasurementTimestamp : node.LatestPacketTimestamp) : null;
  const deviceLostTimestamp = lostSince ? (new Date(lostSince)).toLocaleString('sv-SE').replace('T', ' ') : "--";
  const outdatedMeasurementsMessage = 'Outdated measurements';
  const deviceLostMessage = "Device lost. Since: " + deviceLostTimestamp;
  const batteryWeakOrEmpty = node.SupplyState == 'battery_weak' || node.SupplyState == 'battery_empty';
  const maintenanceMessage = deviceDamage || batteryWeakOrEmpty ? "Maintenance needed" : (!ObstructionStatus && !isMeasurementsOutdated && !isDeviceLost ? "Node OK" : null);
  const outOfRangeMessage = outOfRange ? "Measured value out of range" : null;
  const iconMaintenance = deviceDamage ? <WarningIcon /> : null;

  const deviceDamageTimestamp = deviceDamagedFrom ? (new Date(deviceDamagedFrom)).toLocaleString('sv-SE').replace('T', ' ') : null;
  const deviceDamagedInfoDict = {
    'inclinometer': 'Inclinometer error',
    'rangefinder': 'Rangefinder error',
    'temperature_sensor1': 'Temperature sensor 1 error',
    'temperature_sensor2': 'Temperature sensor 2 error',
    'voltage_sensor1': 'Voltage sensor 1 error',
    'voltage_sensor2': 'Voltage sensor 2 error'
  };
  // calculate obstructing value:
  let baseDistanceCorrectionValue;
  if (BaseDistanceCorrection !== null && ObstructionStatus !== 0) {
    if (Math.abs(BaseDistanceCorrection) < 99) {
      baseDistanceCorrectionValue = `: ca. ${(Math.abs(BaseDistanceCorrection) / 10).toFixed()}cm`;
    } else if (Math.abs(BaseDistanceCorrection) < 999) {
      baseDistanceCorrectionValue = `: ca. ${(Math.abs(BaseDistanceCorrection) / 10).toFixed()}cm`
    } else if (Math.abs(BaseDistanceCorrection) < 9999) {
      baseDistanceCorrectionValue = `: ca. ${(Math.abs(BaseDistanceCorrection) / 1000).toFixed(1)}m`
    } else {
      baseDistanceCorrectionValue = `: ca. ${(Math.abs(BaseDistanceCorrection) / 1000).toFixed()}m`
    }
  }
  const acceleratedMessage = isNodeAccelerated ? "Accelerated mode. Until: " + new Date(AcceleratedUntil).toLocaleString('sv-SE').replace('T', ' ') : null;


  if (isDeviceLost == null) {
    return (
        <CardFooter stats style={cardFooterStyle} data-testid="nodeStatusFooter">
        </CardFooter>
    );
  }
  if (deviceDamage) {
    return (
      <CardFooter stats style={cardFooterStyle} data-testid="nodeStatusFooter">
        { isNodeAccelerated ?
          <TextLoop interval={5000} adjustingSpeed={500} className="node-text-loop">
          <div style={longTextStyle}>
            <SystemFailureIcon color={"error"} />
              Node not OK: {deviceDamagedInfoDict[deviceDamagedInfo]}. Since: {deviceDamageTimestamp}
          </div>
          <div style={nodeAcceleratedStyle}>
            {acceleratedMessage}
          </div>
          {outOfRange ?
              <div style={deviceLostStyle}>
                {outOfRangeMessage}
              </div> 
          : null}
          </TextLoop>        
        :
          <TextLoop>
              <div style={longTextStyle}>
              <SystemFailureIcon color={"error"} />
                Node not OK: {deviceDamagedInfoDict[deviceDamagedInfo]}. Since: {deviceDamageTimestamp}
              </div>
              {outOfRange ?
                <div style={deviceLostStyle}>
                  {outOfRangeMessage}
                </div> 
              : null}
          </TextLoop>

      }
      </CardFooter>
    );
  }
  return (
      <CardFooter stats style={cardFooterStyle} data-testid="nodeStatusFooter">
        <GridContainer style={stats} direction="row">
          {ObstructionStatus !== 0 && ObstructionStatus !== null
            ? 
            //obstructed
            <>
              <GridItem xs={7} style={{ paddingRight: "0px" }}>
                <TextLoop interval={5000} adjustingSpeed={500} className="node-text-loop">
                  { maintenanceMessage !== null ?
                    <div style={isDeviceLost ? deviceLostStyle : {}}>
                      {iconMaintenance}{" "}
                      {maintenanceMessage}
                    </div>
                    : null
                  }
                  { isDeviceLost ?
                    <div style={deviceLostStyle}>
                    {deviceLostMessage}
                    </div>
                    : null
                  }
                  { isMeasurementsOutdated ?
                    <div style={isMeasurementsOutdated ? deviceLostStyle : {}}>
                      {outdatedMeasurementsMessage}
                    </div>
                    : null
                  }
                  <div style={textLoopStyle}>
                    {`Node obstructed`}{BaseDistanceCorrection !== 0 ? baseDistanceCorrectionValue : ""}
                  </div>
                  {isNodeAccelerated ?
                    <div style={nodeAcceleratedStyle}>
                      {acceleratedMessage}
                    </div> : null 
                  }
                  {outOfRange ?
                  <div style={deviceLostStyle}>
                    {outOfRangeMessage}
                  </div> 
                  : null}
                </TextLoop>
              </GridItem>
            </>
            : //not obstructed
            <>
              <GridItem xs={7} style={{ paddingRight: "0px" }}>
              {isNodeAccelerated ? 
                // not obstructed and accelerated
                <TextLoop interval={5000} adjustingSpeed={500} className="node-text-loop">
                  { maintenanceMessage !== null ?
                    <div style={isDeviceLost ? deviceLostStyle : {}}>
                      {iconMaintenance}{" "}
                      {maintenanceMessage}
                    </div>
                    : null
                  }
                  { isDeviceLost ?
                    <div style={deviceLostStyle}>
                    {deviceLostMessage}
                    </div>
                    : null
                  }
                  { isMeasurementsOutdated ?
                    <div style={isMeasurementsOutdated ? deviceLostStyle : {}}>
                      {outdatedMeasurementsMessage}
                    </div>
                    : null
                  }
                  <div style={nodeAcceleratedStyle}>
                    {acceleratedMessage}
                  </div>
                  {outOfRange ?
                  <div style={deviceLostStyle}>
                    {outOfRangeMessage}
                  </div> 
                  : null}
                  </TextLoop>
              : 
               // not obstructed and not accelerated
              <TextLoop interval={5000} adjustingSpeed={500} className="node-text-loop">
                { maintenanceMessage !== null ?
                  <div style={isDeviceLost ? deviceLostStyle : {}}>
                    {iconMaintenance}{" "}
                    {maintenanceMessage}
                  </div>
                  : null
                }
                { isDeviceLost ?
                  <div style={deviceLostStyle}>
                  {deviceLostMessage}
                  </div>
                  : null
                }
                { isMeasurementsOutdated ?
                  <div style={isMeasurementsOutdated ? deviceLostStyle : {}}>
                    {outdatedMeasurementsMessage}
                  </div>
                  : null
                }
                {outOfRange ?
                <div style={deviceLostStyle}>
                  {outOfRangeMessage}
                </div> 
              : null}
              </TextLoop>
              }
              </GridItem>
            </>
          }
        </GridContainer>
      </CardFooter>
  );
}

export default NodeStatusFooter;